import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Shield, Download, Search, RefreshCw, Eye, X, Building2, Users, ClipboardCheck, FileText } from "lucide-react";
import { fmtDate } from "../pages/crm/shared";

const POLICY_TYPE_LABEL = {
    default: { label: "Default", cls: "bg-ink-900 text-ink-300 border-white/15" },
    custom_text: { label: "Testo custom", cls: "bg-purple-900/30 text-purple-200 border-purple-700/40" },
    custom_pdf: { label: "PDF custom", cls: "bg-blue-900/30 text-blue-200 border-blue-700/40" },
};

export default function AdminPrivacy() {
    const [view, setView] = useState("agencies");
    return (
        <div data-testid="admin-privacy" className="space-y-6">
            <div className="flex flex-wrap items-end gap-4 justify-between">
                <div>
                    <div className="text-[10px] uppercase tracking-wider text-gold flex items-center gap-2">
                        <Shield size={13} /> Gestione Privacy GDPR
                    </div>
                    <h2 className="font-serif text-2xl text-ink-50 mt-1">Panoramica policy piattaforma</h2>
                    <p className="text-ink-400 text-xs mt-1">Monitora lo stato delle policy delle agenzie e i consensi raccolti in tutta la piattaforma.</p>
                </div>
                <div className="flex border border-white/15">
                    <TabPill id="agencies" active={view === "agencies"} onClick={setView}>Agenzie</TabPill>
                    <TabPill id="consents" active={view === "consents"} onClick={setView}>Log consensi</TabPill>
                </div>
            </div>
            {view === "agencies" ? <AgenciesView /> : <ConsentsView />}
        </div>
    );
}

function TabPill({ id, active, onClick, children }) {
    return (
        <button
            data-testid={`admin-privacy-tab-${id}`}
            onClick={() => onClick(id)}
            className={`px-4 py-2 text-xs uppercase tracking-wider transition-colors ${active ? "bg-gold text-ink-950" : "text-ink-300 hover:text-ink-50 hover:bg-white/5"}`}
        >
            {children}
        </button>
    );
}

// ============================================================
// Agencies view
// ============================================================
function AgenciesView() {
    const [data, setData] = useState(null);
    const [q, setQ] = useState("");
    const [filterType, setFilterType] = useState("");

    const load = useCallback(async () => {
        try {
            const r = await api.get("/admin/privacy/agencies");
            setData(r.data);
        } catch { setData({ items: [], counts: {} }); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const filtered = useMemo(() => {
        if (!data) return [];
        const rx = q.trim().toLowerCase();
        return (data.items || []).filter((a) => {
            if (filterType && a.policy_type !== filterType) return false;
            if (rx && !(a.agency_name || "").toLowerCase().includes(rx) && !(a.agency_email || "").toLowerCase().includes(rx)) return false;
            return true;
        });
    }, [data, q, filterType]);

    if (!data) return <div className="text-ink-400 text-sm py-12 text-center">Caricamento agenzie...</div>;
    const c = data.counts || {};

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <StatCard label="Agenzie totali" value={c.total_agencies} icon={<Building2 size={13} />} testid="stat-agencies-total" />
                <StatCard label="Con policy default" value={c.using_default} icon={<Shield size={13} />} testid="stat-agencies-default" />
                <StatCard label="Testo custom" value={c.using_custom_text} icon={<FileText size={13} />} testid="stat-agencies-text" accent="text-purple-300" />
                <StatCard label="PDF custom" value={c.using_custom_pdf} icon={<FileText size={13} />} testid="stat-agencies-pdf" accent="text-blue-300" />
                <StatCard label="Consensi tot." value={c.total_consents} icon={<ClipboardCheck size={13} />} testid="stat-agencies-consents" accent="text-gold" />
            </div>

            <div className="flex flex-wrap gap-2 items-center border border-white/10 bg-ink-900/40 p-4">
                <div className="relative flex-1 min-w-[220px]">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-500" />
                    <input
                        data-testid="admin-privacy-search"
                        placeholder="Cerca agenzia (nome o email)..."
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        className="w-full bg-transparent border border-white/15 pl-9 pr-3 py-2 text-sm text-ink-100 outline-none focus:border-gold"
                    />
                </div>
                <select data-testid="admin-privacy-filter-type" value={filterType} onChange={(e) => setFilterType(e.target.value)} className="bg-ink-950 border border-white/15 px-3 py-2 text-sm text-ink-100 outline-none focus:border-gold">
                    <option value="">Tutti i tipi</option>
                    <option value="default">Default</option>
                    <option value="custom_text">Testo custom</option>
                    <option value="custom_pdf">PDF custom</option>
                </select>
                <button onClick={load} className="cta-outline px-3 py-2 text-[11px] flex items-center gap-1.5" data-testid="admin-privacy-refresh">
                    <RefreshCw size={12} /> Aggiorna
                </button>
            </div>

            <div className="border border-white/10 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-ink-950 text-[10px] uppercase tracking-wider text-ink-400">
                        <tr>
                            <th className="px-3 py-2.5 text-left">Agenzia</th>
                            <th className="px-3 py-2.5 text-left">Email</th>
                            <th className="px-3 py-2.5 text-left">Policy</th>
                            <th className="px-3 py-2.5 text-left">Ultimo aggiornamento</th>
                            <th className="px-3 py-2.5 text-right">Consensi</th>
                            <th className="px-3 py-2.5 text-left">Ultimo consenso</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 && (
                            <tr><td colSpan={6} className="text-center text-ink-500 py-8 text-xs italic">Nessuna agenzia corrisponde ai filtri.</td></tr>
                        )}
                        {filtered.map((a) => {
                            const meta = POLICY_TYPE_LABEL[a.policy_type] || POLICY_TYPE_LABEL.default;
                            return (
                                <tr key={a.agency_id} data-testid={`admin-agency-row-${a.agency_id}`} className="border-t border-white/5 hover:bg-white/5">
                                    <td className="px-3 py-2.5 text-ink-100">{a.agency_name}</td>
                                    <td className="px-3 py-2.5 text-ink-300 text-[12px]">{a.agency_email}</td>
                                    <td className="px-3 py-2.5">
                                        <span className={`text-[9px] uppercase tracking-wider px-2 py-0.5 border ${meta.cls}`}>{meta.label} · {a.policy_version}</span>
                                    </td>
                                    <td className="px-3 py-2.5 text-ink-400 text-[11px]">{a.policy_updated_at ? fmtDate(a.policy_updated_at) : "—"}</td>
                                    <td className="px-3 py-2.5 text-right font-serif text-gold">{a.consents_count}</td>
                                    <td className="px-3 py-2.5 text-ink-400 text-[11px]">{a.consents_last_at ? fmtDate(a.consents_last_at) : "—"}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ============================================================
// Global consents log
// ============================================================
function ConsentsView() {
    const [data, setData] = useState(null);
    const [q, setQ] = useState("");
    const [df, setDf] = useState("");
    const [dt, setDt] = useState("");
    const [policyType, setPolicyType] = useState("");
    const [agencyId, setAgencyId] = useState("");
    const [openConsent, setOpenConsent] = useState(null);

    const load = useCallback(async () => {
        try {
            const params = {};
            if (q) params.q = q;
            if (df) params.date_from = df;
            if (dt) params.date_to = dt;
            if (policyType) params.policy_type = policyType;
            if (agencyId) params.agency_id = agencyId;
            const r = await api.get("/admin/privacy/consents", { params });
            setData(r.data);
        } catch { setData({ items: [], total_matching: 0 }); }
    }, [q, df, dt, policyType, agencyId]);
    useEffect(() => { load(); }, [load]);

    const csvUrl = useMemo(() => {
        const p = new URLSearchParams();
        if (df) p.set("date_from", df);
        if (dt) p.set("date_to", dt);
        if (policyType) p.set("policy_type", policyType);
        if (agencyId) p.set("agency_id", agencyId);
        if (q) p.set("q", q);
        const qs = p.toString();
        return `${process.env.REACT_APP_BACKEND_URL}/api/admin/privacy/consents/export.csv${qs ? "?" + qs : ""}`;
    }, [df, dt, policyType, agencyId, q]);

    return (
        <div className="space-y-4">
            <div className="border border-white/10 bg-ink-900/40 p-4">
                <div className="grid grid-cols-2 md:grid-cols-6 gap-2 items-end">
                    <div className="md:col-span-2 relative">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-500" />
                        <input
                            data-testid="admin-consents-search"
                            placeholder="Cerca nome, email, agenzia, IP..."
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            className="w-full bg-transparent border border-white/15 pl-9 pr-3 py-2 text-sm text-ink-100 outline-none focus:border-gold"
                        />
                    </div>
                    <label className="text-[10px] uppercase tracking-wider text-ink-400 flex flex-col gap-1">
                        Dal
                        <input data-testid="admin-consents-from" type="date" value={df} onChange={(e) => setDf(e.target.value)} className="bg-ink-950 border border-white/15 px-2 py-1.5 text-sm text-ink-100 focus:border-gold outline-none" />
                    </label>
                    <label className="text-[10px] uppercase tracking-wider text-ink-400 flex flex-col gap-1">
                        Al
                        <input data-testid="admin-consents-to" type="date" value={dt} onChange={(e) => setDt(e.target.value)} className="bg-ink-950 border border-white/15 px-2 py-1.5 text-sm text-ink-100 focus:border-gold outline-none" />
                    </label>
                    <label className="text-[10px] uppercase tracking-wider text-ink-400 flex flex-col gap-1">
                        Tipo policy
                        <select data-testid="admin-consents-type" value={policyType} onChange={(e) => setPolicyType(e.target.value)} className="bg-ink-950 border border-white/15 px-2 py-1.5 text-sm text-ink-100 focus:border-gold outline-none">
                            <option value="">Tutte</option>
                            <option value="default">Default</option>
                            <option value="custom_text">Testo custom</option>
                            <option value="custom_pdf">PDF custom</option>
                        </select>
                    </label>
                    <label className="text-[10px] uppercase tracking-wider text-ink-400 flex flex-col gap-1">
                        Agency ID
                        <input data-testid="admin-consents-agency" placeholder="opz." value={agencyId} onChange={(e) => setAgencyId(e.target.value)} className="bg-ink-950 border border-white/15 px-2 py-1.5 text-sm text-ink-100 focus:border-gold outline-none font-mono text-[11px]" />
                    </label>
                </div>
                <div className="flex items-center gap-2 mt-3">
                    <div className="text-[11px] text-ink-400">
                        {data ? <><span className="text-ink-100 font-medium">{data.total_matching}</span> consensi corrispondenti · {data.count} visualizzati</> : "Caricamento..."}
                    </div>
                    <div className="flex-1" />
                    <a
                        href={csvUrl}
                        data-testid="admin-consents-export-csv"
                        className="cta-gold px-4 py-2 text-[11px] flex items-center gap-2"
                    >
                        <Download size={12} /> Esporta CSV
                    </a>
                </div>
            </div>

            <div className="border border-white/10 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-ink-950 text-[10px] uppercase tracking-wider text-ink-400">
                        <tr>
                            <th className="px-3 py-2.5 text-left">Data</th>
                            <th className="px-3 py-2.5 text-left">Agenzia</th>
                            <th className="px-3 py-2.5 text-left">Nome</th>
                            <th className="px-3 py-2.5 text-left">Email</th>
                            <th className="px-3 py-2.5 text-left">IP</th>
                            <th className="px-3 py-2.5 text-left">Policy</th>
                            <th className="px-3 py-2.5 text-left">Fonte</th>
                            <th className="px-3 py-2.5"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {data?.items?.length === 0 && (
                            <tr><td colSpan={8} className="text-center text-ink-500 py-8 text-xs italic">Nessun consenso corrisponde ai filtri.</td></tr>
                        )}
                        {data?.items?.map((c) => {
                            const meta = POLICY_TYPE_LABEL[c.policy_type] || POLICY_TYPE_LABEL.default;
                            return (
                                <tr key={c.id} data-testid={`admin-consent-row-${c.id}`} className="border-t border-white/5 hover:bg-white/5">
                                    <td className="px-3 py-2.5 text-ink-300 text-[12px]">{fmtDate(c.created_at)}</td>
                                    <td className="px-3 py-2.5 text-ink-100 text-[12px]">{c.agency_name}</td>
                                    <td className="px-3 py-2.5 text-ink-100">{c.contact_name || "—"}</td>
                                    <td className="px-3 py-2.5 text-ink-300 text-[12px]">{c.contact_email || "—"}</td>
                                    <td className="px-3 py-2.5 text-ink-400 font-mono text-[11px]">{c.ip || "—"}</td>
                                    <td className="px-3 py-2.5">
                                        <span className={`text-[9px] uppercase tracking-wider px-2 py-0.5 border ${meta.cls}`}>{meta.label} · {c.policy_version}</span>
                                    </td>
                                    <td className="px-3 py-2.5 text-ink-400 text-[11px]">{c.source || "—"}</td>
                                    <td className="px-3 py-2.5">
                                        <button data-testid={`admin-consent-view-${c.id}`} onClick={() => setOpenConsent(c.id)} className="text-ink-400 hover:text-gold" title="Vedi snapshot policy"><Eye size={13} /></button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {openConsent && <ConsentDetailOverlay consentId={openConsent} onClose={() => setOpenConsent(null)} />}
        </div>
    );
}

function ConsentDetailOverlay({ consentId, onClose }) {
    const [c, setC] = useState(null);
    useEffect(() => {
        api.get(`/admin/privacy/consents/${consentId}`).then((r) => setC(r.data)).catch(() => onClose());
    }, [consentId, onClose]);
    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={onClose} data-testid="admin-consent-detail">
            <div className="relative w-full max-w-3xl max-h-[85vh] border border-gold/30 bg-ink-950 flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
                    <div className="font-serif text-lg text-ink-50 flex items-center gap-2">
                        <ClipboardCheck size={15} className="text-gold" /> Dettaglio consenso
                    </div>
                    <button onClick={onClose} data-testid="admin-consent-detail-close" className="text-ink-400 hover:text-ink-50" aria-label="Chiudi"><X size={16} /></button>
                </div>
                {!c ? <div className="p-8 text-ink-400 text-sm">Caricamento...</div> : (
                    <div className="flex-1 overflow-y-auto p-6 text-sm space-y-3">
                        <Row k="Data e ora" v={fmtDate(c.created_at)} />
                        <Row k="Agenzia" v={`${c.agency_name} (${c.agency_id})`} />
                        <Row k="Contatto" v={`${c.contact_name || "—"} · ${c.contact_email || "—"}`} />
                        <Row k="IP" v={c.ip} mono />
                        <Row k="User Agent" v={c.user_agent} mono small />
                        <Row k="Tipo policy" v={`${POLICY_TYPE_LABEL[c.policy_type]?.label || c.policy_type} · versione ${c.policy_version}`} />
                        <Row k="Fonte" v={c.source} />
                        {c.property_id && <Row k="Immobile" v={c.property_id} mono />}
                        <div>
                            <div className="text-[10px] uppercase tracking-wider text-gold mt-4 mb-2">Snapshot integrale policy al momento del consenso</div>
                            <pre className="whitespace-pre-wrap font-mono text-[11px] leading-relaxed text-ink-100 border border-white/10 bg-ink-950 p-4 max-h-[40vh] overflow-y-auto">
{c.policy_content || (c.policy_pdf_filename ? `[PDF: ${c.policy_pdf_filename}]` : "—")}
                            </pre>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function Row({ k, v, mono, small }) {
    return (
        <div className="flex gap-3">
            <div className="text-[10px] uppercase tracking-wider text-ink-400 w-32 shrink-0 pt-0.5">{k}</div>
            <div className={`text-ink-100 flex-1 ${mono ? "font-mono" : ""} ${small ? "text-[11px]" : ""}`}>{v || "—"}</div>
        </div>
    );
}

function StatCard({ label, value, icon, accent, testid }) {
    return (
        <div className="border border-white/10 bg-ink-900/40 p-4" data-testid={testid}>
            <div className={`flex items-center gap-2 text-xs ${accent || "text-ink-400"}`}>{icon} <span className="uppercase tracking-wider text-[9px]">{label}</span></div>
            <div className="font-serif text-2xl text-ink-50 mt-2">{value ?? 0}</div>
        </div>
    );
}
